import {
  CurrencyFormatted,
  TooltipContent,
  TooltipTrigger,
  CardContent,
  CardHeader,
  CardTitle,
  Tooltip,
  Loading,
  Button,
  Card,
} from '@ui/components';
import { CheckImpersonation, HasPermission } from '@shared/components';
import { LabeledText } from '@pages/cost-centers/components';
import { ComponentPropsWithoutRef, FC } from 'react';
import { Wallet, Info } from '@ui/components/icons';
import { useTranslation } from 'react-i18next';
import { cn } from '@ui/utils';

import { CostCenterAccountTopUpsDrawer } from '../../cost-center-account-top-ups-drawer/cost-center-account-top-ups-drawer';
import { CostCenterAccountDetails } from '../../cost-center-account-details/cost-center-account-details';
import { useCostCenterAccount } from './use-cost-center-account';

export const CostCenterAccount: FC<ComponentPropsWithoutRef<typeof Card>> = ({
  className,
  ...props
}) => {
  const { t } = useTranslation();
  const { data, isLoading, costCenterId } = useCostCenterAccount();

  return (
    <Card className={cn('flex flex-col', className)} {...props}>
      <CardHeader className="justify-between">
        <div className="flex items-center gap-2">
          <Wallet className="w-4.5 h-4.5 text-weak" />
          <CardTitle className="flex items-center gap-2">
            {t('COST_CENTER.ACCOUNT.TITLE')}
          </CardTitle>
          <Tooltip>
            <TooltipTrigger>
              <Info />
            </TooltipTrigger>
            <TooltipContent>{t('COST_CENTER.ACCOUNT.TOOLTIP')}</TooltipContent>
          </Tooltip>
        </div>
        {!!costCenterId && (
          <HasPermission permissionFeature="COST_CENTER_ACCOUNT_SEE_DETAILS" permissionType="READ">
            <CostCenterAccountDetails costCenterId={costCenterId}>
              <Button disabled={isLoading} variant="link">
                {t('COST_CENTER.DETAILS')}
              </Button>
            </CostCenterAccountDetails>
          </HasPermission>
        )}
      </CardHeader>

      <CardContent className="flex-1 flex flex-col gap-4">
        <Loading isLoading={isLoading} className="h-full">
          <div className="flex-1">
            <LabeledText label={t('COST_CENTER.ACCOUNT.ACCOUNT_BALANCE')} isLoading={isLoading}>
              <CurrencyFormatted currency={data?.currency} value={data?.amount || 0} />
            </LabeledText>
          </div>
          {!!costCenterId && (
            <CheckImpersonation>
              <HasPermission permissionFeature="COST_CENTER_ACCOUNT_TOP_UP" permissionType="WRITE">
                <CostCenterAccountTopUpsDrawer costCenterId={costCenterId}>
                  <Button isLoading={isLoading} disabled={isLoading} variant="tertiary" wide>
                    {t('COST_CENTER.ACCOUNT.TOP_UP_ACCOUNT')}
                  </Button>
                </CostCenterAccountTopUpsDrawer>
              </HasPermission>
            </CheckImpersonation>
          )}
        </Loading>
      </CardContent>
    </Card>
  );
};
